import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { AnonymousPaymentPageContent } from "Components/AnonymousCustomer/Payment/AnonymousPaymentPageContent";
import {
  getPaymentStepByUrlParam,
  type PaymentStep,
} from "Components/AnonymousCustomer/Payment/config";
import { MissingTransactionIdContent } from "Components/AnonymousCustomer/Payment/PaymentDetail/MissingTransactionIdContent";
import { AnonymousLayout } from "Components/Layout/AnonymousLayout";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { useSearchParams } from "Navigation/useSearchParams";
import { Resources, useResource } from "Translations/Resources";
import { getCampaignLogo } from "Utils/CampaignLogo";
import { storeCampaignForTransaction } from "Utils/CampaignStorage";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const PageResources = Resources.Customer.Pay;

export const AnonymousCustomerPaymentPayPage = () => {
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const stepFromUrl = getPaymentStepByUrlParam(searchParams.get("step"));
  // ?step= is ignored without an ID: this state is seeded once, and the page stays
  // mounted when the visitor supplies an ID, so a step seeded here would survive
  // into the payment flow and skip the detail step.
  const initialStep: PaymentStep = id && stepFromUrl ? stepFromUrl : "detail";
  const [activeStep, setActiveStep] = useState<PaymentStep>(initialStep);
  const { t } = useResource();
  const user = useLoggedInUser();
  const _prefetch = useBankingAccounts(undefined, !!user);

  const affiliateCode = searchParams.get("affiliateCode") ?? undefined;
  const campaignCode = searchParams.get("campaign") ?? undefined;
  const campaignLogo = getCampaignLogo(campaignCode);

  // Store campaign data in localStorage when present
  useEffect(() => {
    if (id && campaignCode) {
      storeCampaignForTransaction(id, campaignCode);
    }
  }, [id, campaignCode]);

  if (!id) {
    return (
      <AnonymousLayout
        title={t(PageResources.Title)}
        secondaryLogo={campaignLogo}
        isFullHeight
      >
        <StyledPageWrapper marginX="auto">
          <MissingTransactionIdContent />
        </StyledPageWrapper>
      </AnonymousLayout>
    );
  }

  return (
    <AnonymousLayout
      title={t(PageResources.Title)}
      secondaryLogo={campaignLogo}
      isFullHeight={activeStep === "detail"}
    >
      <StyledPageWrapper marginX="auto">
        <AnonymousPaymentPageContent
          transactionPublicID={id}
          activeStep={activeStep}
          onActiveStepChange={setActiveStep}
          affiliateCode={affiliateCode}
          campaignCode={campaignCode}
        />
      </StyledPageWrapper>
    </AnonymousLayout>
  );
};

export default AnonymousCustomerPaymentPayPage;
